import {
  IRemitHeadlineCell,
  RemitAccuracyOutcome,
  RemitPickKind,
  RemitScoredBenefit,
} from "types/RemitAccuracy/RemitAccuracy";

/**
 * CLE-2818 — every label and number format on the Remit accuracy tab, in one place so the tests can
 * pin them.
 */

export const REMIT_BENEFIT_LABELS: Record<RemitScoredBenefit, string> = {
  COINSURANCE_PERCENT: "Co-insurance",
  FLAT_COPAY: "Facility copay",
  PROFESSIONAL_COPAY: "Physician copay",
};

export const REMIT_KIND_LABELS: Record<RemitPickKind, string> = {
  REPLAY: "Today's config (re-run nightly)",
  QUOTED: "At time of check",
};

export const REMIT_OUTCOME_LABELS: Record<RemitAccuracyOutcome, string> = {
  MATCH: "Match",
  ENGINE_DIFFERENT: "Miss",
  ENGINE_ZERO: "Miss · engine $0",
  ENGINE_NO_VALUE: "Miss · no engine value",
  NOT_CONFIGURED: "Not configured",
  REMITS_DISAGREE: "Remits disagree",
};

/**
 * What an empty pick kind is waiting for. QUOTED rows are written by live checks from the deploy on;
 * REPLAY rows only exist once the nightly job has run.
 */
export const emptyKindText = (kind: RemitPickKind) =>
  kind === "QUOTED"
    ? "Collecting since deploy"
    : "Waiting for the first nightly run";

/** The remit share a not-configured count is about: a copay for the copays, co-insurance otherwise. */
export const remitShareWording = (benefit: RemitScoredBenefit) =>
  benefit === "COINSURANCE_PERCENT" ? "with co-insurance" : "with a copay";

export const READ_FROM_LABELS: Record<string, string> = {
  LINE: "Line",
  SUMMARY: "Service-line summary",
  INDEX: "Earlier check (history)",
  FORCED: "Fixed payer rule",
  OVERRIDE: "Payer override",
  NONE: "Nothing",
};

export const REMIT_EXCLUSION_LABELS: Record<string, string> = {
  NO_PRIMARY_CHECK: "Estimate has no primary eligibility check",
  CLAIM_PAYER_UNRESOLVED: "Payer not resolved on the claim",
  CLAIM_IS_ORDER_SECONDARY_PAYER: "Claim is the estimate's secondary payer",
  PAYER_DIFFERENT: "Different payer from the estimate",
  REPROCESS_OR_REVERSAL_HISTORY: "Reprocessed or reversed",
  SECONDARY_EVENT: "Has a secondary-payer remit",
  LOW_ORDER_MATCH_CONFIDENCE: "Weak claim → estimate match (< 0.9)",
  DIFFERENT_PLAN_YEAR: "Check in a different plan year",
  NO_LINE_ITEMS: "Remit has no service lines",
};

export const exclusionLabel = (reason: string) =>
  REMIT_EXCLUSION_LABELS[reason] ?? reason;

export const percentOf = (cell: Pick<IRemitHeadlineCell, "scored" | "match">) =>
  cell.scored === 0 ? null : (100 * cell.match) / cell.scored;

/** "95.0% (799 / 841)" — a percentage is never shown without its sample size. */
export const accuracyText = (
  cell?: Pick<IRemitHeadlineCell, "scored" | "match">
) => {
  if (!cell || cell.scored === 0) return "not measured";
  return `${(percentOf(cell) as number).toFixed(
    1
  )}% (${cell.match.toLocaleString()} / ${cell.scored.toLocaleString()})`;
};

export const formatRemitValue = (
  value: number | null | undefined,
  benefit: RemitScoredBenefit
) => {
  if (value === null || value === undefined) return "—";
  if (benefit === "COINSURANCE_PERCENT") return `${value}%`;
  return Number.isInteger(value)
    ? `$${value.toLocaleString("en-US")}`
    : `$${value.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
};

/** "$150 – $1,850", "$0", or "no value" when the engine had none on every check. */
export const formatRange = (
  min: number | null,
  max: number | null,
  benefit: RemitScoredBenefit,
  hadNoValue = false
) => {
  const parts: string[] = [];
  if (min !== null && max !== null) {
    parts.push(
      min === max
        ? formatRemitValue(min, benefit)
        : `${formatRemitValue(min, benefit)} – ${formatRemitValue(
            max,
            benefit
          )}`
    );
  }
  if (hadNoValue) parts.push("no value");
  return parts.join(" / ") || "—";
};

const iso = (date: Date) => date.toISOString().slice(0, 10);

export type DateOfServicePreset =
  | "ALL"
  | "LAST_90_DAYS"
  | "LAST_12_MONTHS"
  | "THIS_YEAR"
  | "LAST_YEAR";

export const DATE_OF_SERVICE_OPTIONS: {
  value: DateOfServicePreset;
  label: string;
}[] = [
  { value: "ALL", label: "All time" },
  { value: "LAST_90_DAYS", label: "Last 90 days" },
  { value: "LAST_12_MONTHS", label: "Last 12 months" },
  { value: "THIS_YEAR", label: "This calendar year" },
  { value: "LAST_YEAR", label: "Last calendar year" },
];

export const dateOfServiceRange = (
  preset: DateOfServicePreset,
  today: Date = new Date()
): { dosFrom?: string; dosTo?: string } => {
  const year = today.getUTCFullYear();
  switch (preset) {
    case "LAST_90_DAYS":
      return { dosFrom: iso(new Date(today.getTime() - 90 * 86400000)) };
    case "LAST_12_MONTHS": {
      const from = new Date(today);
      from.setUTCFullYear(year - 1);
      return { dosFrom: iso(from) };
    }
    case "THIS_YEAR":
      return { dosFrom: `${year}-01-01`, dosTo: `${year}-12-31` };
    case "LAST_YEAR":
      return { dosFrom: `${year - 1}-01-01`, dosTo: `${year - 1}-12-31` };
    default:
      return {};
  }
};

export const lineLabel = (line: {
  message: string[];
  planCoverageDescription: string | null;
  serviceName: string;
}) =>
  line.message.length
    ? line.message.join(" / ")
    : line.planCoverageDescription ?? line.serviceName;
